import type { CmsPdfBlock, CmsPdfPage } from './assessment-cms-pdf';

const text = (element: Element | null, preserveBreaks = false) => {
  if (!element) return '';
  const copy = element.cloneNode(true) as Element;
  copy.querySelectorAll('.scChromeData, code[type="text/sitecore"], [scwatermark="true"]').forEach(node => node.remove());
  copy.querySelectorAll('br').forEach(br => br.replaceWith(preserveBreaks ? '\n' : ' '));
  return copy.textContent?.replace(preserveBreaks ? /[^\S\n]+/g : /\s+/g, ' ').trim() || '';
};

export function readCmsAssessment(root: HTMLElement): CmsPdfPage[] {
  return Array.from(root.querySelectorAll<HTMLElement>('.Interactive-Pdf-Page')).map(
    (page, pageIndex) => {
      const blocks: CmsPdfBlock[] = [];
      const walk = (element: HTMLElement) => {
        if (element.matches('header, footer, script, style')) return;
        if (element.matches('.Single-Line-Field')) {
          const input = element.querySelector<HTMLInputElement>('input');
          blocks.push({
            kind: 'field',
            role: element.matches('.Single-Line-Field') ? 'identity' : 'notes',
            text: text(element.querySelector('label')),
            value: input?.value || ''
          });
        } else if (element.matches('.Radio-Button-Field')) {
          blocks.push({
            kind: 'question',
            text: [
              text(element.querySelector('legend')),
              text(element.querySelector('.Radio-Button-Field-description'))
            ]
              .filter(Boolean)
              .join('\n'),
            choices: Array.from(
              element.querySelectorAll<HTMLInputElement>('input[type=radio]')
            ).map(input => ({
              text:
                text(input.closest('label')?.querySelector('.Radio-Button-Option-text') || null) ||
                input.value,
              selected: input.checked
            }))
          });
        } else if (element.matches('.Multiple-Checkbox-Field')) {
          blocks.push({
            kind: 'checks',
            text: text(element.querySelector('legend')),
            choices: Array.from(
              element.querySelectorAll<HTMLInputElement>('input[type=checkbox]')
            ).map(input => ({
              text: text(input.closest('label')?.querySelector('.Check-Box-Option-text') || null),
              selected: input.checked
            }))
          });
          element.querySelectorAll<HTMLTextAreaElement>('textarea').forEach(input => {
            const label = input.id ? Array.from(element.querySelectorAll('label')).find(label => label.htmlFor === input.id) : null;
            blocks.push({ kind: 'field', role: 'notes', text: text(label || null), value: input.value });
          });
        } else if (
          element.matches('.Multiple-Checkbox-Table-Field-table, .Catheter-Features-Table')
        ) {
          const chart = element.matches('.Catheter-Features-Table');
          blocks.push({
            kind: chart ? 'chart' : 'table',
            headers: Array.from(element.querySelectorAll('thead th, [role=columnheader]')).map(element => text(element)),
            rows: Array.from(element.querySelectorAll('tbody tr, .Multiple-Checkbox-Table-Field-body [role=row]')).map(row => ({
              cells: Array.from(row.children).filter(cell => cell.matches('th, td, [role=rowheader], [role=cell]')).map(cell =>
                chart && cell.querySelector('div')
                  ? text(cell.querySelector('div'), true)
                  : cell.querySelector('input')
                    ? ''
                    : text(cell, chart)
              ),
              selected: !!row.querySelector<HTMLInputElement>('input')?.checked
            }))
          });
        } else if (element.matches('.Multiple-Checkbox-Table-Field-notes, .Section-notes')) {
          blocks.push({
            kind: 'field',
            role: element.matches('.Single-Line-Field') ? 'identity' : 'notes',
            text: text(element.querySelector('label')),
            value: element.querySelector<HTMLTextAreaElement>('textarea')?.value || ''
          });
        } else if (element.matches('.Section-total')) {
          blocks.push({
            kind: 'field',
            role: element.matches('.Single-Line-Field') ? 'identity' : 'notes',
            text: text(element.querySelector('label')),
            value: text(element.querySelector('output'))
          });
        } else if (element.matches('.Section-title')) {
          const title = element.cloneNode(true) as HTMLElement;
          let references = Array.from(title.querySelectorAll('sup'))
            .map(element => text(element))
            .join(',');
          title.querySelectorAll('sup').forEach(sup => sup.remove());
          let headingText = text(title);
          if (!references && !root.querySelector('.Radio-Button-Field')) {
            const trailingReferences = headingText.match(/(?<=[A-Za-z])(\d(?:,\d)+)$/);
            if (trailingReferences) {
              references = trailingReferences[1];
              headingText = headingText.slice(0, -references.length);
            }
          }
          blocks.push({ kind: 'heading', text: headingText, value: references });
        } else if (
          element.matches(
            '.Section-description, .Section-closing, .Interactive-Pdf-Page-next-page, .QR-Code-Section'
          )
        ) {
          if (element.matches('.QR-Code-Section')) {
            blocks.push({
              kind: 'text',
              role: 'next-step',
              text: text(element.querySelector('.QR-Code-Section-next-step'))
            });
            blocks.push({
              kind: 'text',
              role: 'qr',
              text: text(element.querySelector('.QR-Code-Section-code-label'))
            });
          } else blocks.push({ kind: 'text', role: element.className, text: text(element) });
        } else if (element.matches('.Rich-Text-Section-text') && !element.querySelector('table')) {
          blocks.push({ kind: 'text', text: text(element) });
        } else {
          Array.from(element.children).forEach(child => walk(child as HTMLElement));
        }
      };
      Array.from(page.children).forEach(child => walk(child as HTMLElement));
      const footer = page.querySelector('footer');
      const footerCopy = footer?.cloneNode(true) as HTMLElement | undefined;
      footerCopy?.querySelectorAll('ol').forEach(list => {
        list.textContent = Array.from(list.children)
          .map((item, index) => `${index + 1}. ${text(item)}`)
          .join('\n');
      });
      const disclaimer = footer?.querySelector('.Interactive-Pdf-Page-disclaimer');
      return {
        title: text(page.querySelector('.Interactive-Pdf-Page-title')),
        layout:
          root.querySelectorAll('.Interactive-Pdf-Page').length !== 2
            ? undefined
            : root.querySelector('.Radio-Button-Field')
              ? pageIndex === 0
                ? 'patient-front'
                : 'patient-followup'
              : page.querySelector('.Catheter-Features-Table') ||
                  (pageIndex === 1 && page.querySelector('.Rich-Text-Section'))
                ? 'chart'
                : 'clinician',
        references: Array.from(footer?.querySelectorAll('ol li') || []).map(
          (item, index) => `${index + 1}. ${text(item)}`
        ),
        disclaimer:
          Array.from(footer?.querySelectorAll('.Interactive-Pdf-Page-disclaimer > p') || [])
            .map(element => text(element))
            .join('\n') || (disclaimer && !disclaimer.querySelector('ol') ? text(disclaimer) : ''),
        footerText: (
          Array.from(footer?.querySelectorAll('.Interactive-Pdf-Page-footer-text p') || [])
            .map(element => text(element))
            .join('\n') || text(footer?.querySelector('.Interactive-Pdf-Page-footer-text') || null)
        )
          .replace(/<\s*(?:Hollister Logo|Holister Logo|CE logo)\s*>/gi, '')
          .trim(),
        blocks,
        footer: text(footerCopy || null)
      };
    }
  );
}

export function updateAssessmentQrLayout(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('.QR-Code-Section').forEach(section => {
    const copy = section.querySelector<HTMLElement>('.QR-Code-Section-next-step');
    const empty = !text(copy);
    if (copy && !empty && !copy.querySelector('.scWebEditInput, [contenteditable="true"]')) {
      const walker = copy.ownerDocument.createTreeWalker(copy, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node && !node.textContent?.trim()) node = walker.nextNode();
      const match = node?.textContent?.match(/^(\s*)(next\s+step:)/i);
      if (node && match && node.parentElement?.tagName !== 'STRONG') {
        const strong = copy.ownerDocument.createElement('strong');
        strong.textContent = match[2];
        const value = node.textContent || '';
        node.parentNode?.insertBefore(copy.ownerDocument.createTextNode(match[1]), node);
        node.parentNode?.insertBefore(strong, node);
        node.textContent = value.slice(match[0].length);
      }
    }
    section.classList.toggle('QR-Code-Section--code-only', empty);
    // Preserve editable CMS chrome even when its field has no public text.
    if (copy) copy.hidden = empty && !copy.querySelector('.scWebEditInput, [contenteditable="true"]');
  });
}

const initialized = new WeakSet<HTMLElement>();
export function initializePrintableAssessment(root: HTMLElement) {
  if (initialized.has(root)) return;
  initialized.add(root);
  updateAssessmentQrLayout(root);
  root.addEventListener('input', event => {
    if ((event.target as Element).closest('.QR-Code-Section-next-step')) updateAssessmentQrLayout(root);
  });
  root.querySelector('form')?.addEventListener('submit', event => event.preventDefault());
  // Works with the existing CMS output; no dependence on Storybook fixture IDs.
  root.querySelectorAll<HTMLElement>('.Section').forEach(section => {
    if (
      Array.from(section.querySelectorAll('.Single-Line-Field')).some(
        field => field.closest('.Section') === section
      )
    )
      section.classList.add('Section--identity');
    const output = section.querySelector('output');
    if (!output) return;
    const update = () => {
      const groups = Array.from(section.querySelectorAll('.Radio-Button-Field'));
      const values = groups.map(
        group => group.querySelector<HTMLInputElement>('input[type=radio]:checked')?.value
      );
      output.textContent =
        values.length &&
        values.every(value => value !== undefined && value !== '' && Number.isFinite(Number(value)))
          ? String(values.reduce((sum, value) => sum + Number(value), 0))
          : '—';
    };
    section.addEventListener('change', update);
    update();
  });
  root.querySelectorAll<HTMLElement>('.Multiple-Checkbox-Field').forEach(fieldset => {
    const legend = fieldset.querySelector('legend');
    const heading = fieldset.closest('.Section')?.querySelector('.Section-title');
    if (legend && !text(legend) && heading?.id) {
      legend.remove();
      fieldset.setAttribute('aria-labelledby', heading.id);
    }
  });
  const button = root.querySelector<HTMLButtonElement>('.Printable-Form-Page-download');
  const status = root.querySelector<HTMLElement>('.Printable-Form-Page-status');
  if (!button || !status) return;
  const label = button.textContent || 'Download PDF';
  button.addEventListener('click', async () => {
    if (button.disabled) return;
    const pages = readCmsAssessment(root);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = 'Preparing your A4 PDF.';
    try {
      const { createCmsAssessmentPdf } = await import('./assessment-cms-pdf');
      const { loadAssessmentPdfAssets } = await import('./assessment-pdf-assets');
      const bytes = await createCmsAssessmentPdf(pages, await loadAssessmentPdfAssets());
      const { PDFDocument } = await import('pdf-lib');
      const pageCount = (await PDFDocument.load(bytes)).getPageCount();
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'assessment-a4.pdf';
      root.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      status.textContent = `Your ${pageCount}-page A4 PDF is ready. Open the downloaded file to print it.`;
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      status.textContent = message.includes('WinAnsi')
        ? 'Some characters cannot be included in this PDF. Please use Latin characters and try again.'
        : message || 'The PDF could not be generated. Please try again.';
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  });
}
